/**
 * SPI fallback when provider cap unknown (R$ 15.000).
 */
export const PIX_BATCH_DEFAULT_PER_TRANSACTION_CAP_CENTS = 1_500_000;

/**
 * Effective leg cap: min(provider envelope, SPI max, account per-op, period limit).
 */
export const resolvePixBatchPerTransactionCapCents = (
  providerSplitCapCents,
  accountPerOpCents,
  periodLimitCents,
  spiMaxPerOperationCents,
) => {
  const envelopeCap = Math.trunc(Number(providerSplitCapCents) || 0);
  const spiMax = Math.trunc(Number(spiMaxPerOperationCents) || 0);
  const accountPerOp = Math.trunc(Number(accountPerOpCents) || 0);
  const period = Math.trunc(Number(periodLimitCents) || 0);

  let cap =
    envelopeCap > 0
      ? envelopeCap
      : spiMax > 0
        ? spiMax
        : PIX_BATCH_DEFAULT_PER_TRANSACTION_CAP_CENTS;

  if (spiMax > 0 && envelopeCap <= 0) cap = Math.min(cap, spiMax);
  if (accountPerOp > 0) cap = Math.min(cap, accountPerOp);
  if (period > 0) cap = Math.min(cap, period);
  return cap;
};

/**
 * Splits a Pix lote total into SPI legs (BRL cents). Residual always lands on the last perna.
 */
export const computePixBatchLegs = (totalCents, perTransactionCapCents) => {
  const total = Math.trunc(Number(totalCents) || 0);
  const cap = Math.trunc(Number(perTransactionCapCents) || 0);
  if (total <= 0 || cap <= 0) return [];
  if (total <= cap) {
    return [{ index: 1, count: 1, amountCents: total, isResidual: false }];
  }

  const fullLegs = Math.floor(total / cap);
  const remainder = total % cap;
  const count = remainder === 0 ? fullLegs : fullLegs + 1;
  const legs = [];
  for (let i = 1; i <= fullLegs; i += 1) {
    legs.push({ index: i, count, amountCents: cap, isResidual: false });
  }
  if (remainder > 0) {
    legs.push({ index: count, count, amountCents: remainder, isResidual: true });
  }
  return legs;
};

export const describePixBatchSplit = (totalCents, perTransactionCapCents) => {
  const legs = computePixBatchLegs(totalCents, perTransactionCapCents);
  if (legs.length <= 1) return null;
  const residual = legs.find((leg) => leg.isResidual);
  return {
    count: legs.length,
    capCents: Math.trunc(Number(perTransactionCapCents) || 0),
    residualCents: residual?.amountCents ?? 0,
    legs,
  };
};

/**
 * Banner / createPixBatch when amount needs more than one leg.
 * periodAvailableCents: null skips period check (e.g. limits item missing).
 */
export const resolvePixBatchSplitForAmount = (
  amountCents,
  providerSplitCapCents,
  accountPerOpCents,
  periodLimitCents,
  periodAvailableCents,
  spiMaxPerOperationCents,
) => {
  const amount = Math.trunc(Number(amountCents) || 0);
  if (amount <= 0) return null;

  const availableRaw = periodAvailableCents;
  const available =
    availableRaw == null || availableRaw === ""
      ? null
      : Math.trunc(Number(availableRaw) || 0);
  if (available != null && (available <= 0 || amount > available)) return null;

  const perTxCap = resolvePixBatchPerTransactionCapCents(
    providerSplitCapCents,
    accountPerOpCents,
    periodLimitCents,
    spiMaxPerOperationCents,
  );
  return describePixBatchSplit(amount, perTxCap);
};
